<template>
  <div>
    <div class="page-actions">
      <base-button
        type="info"
        icon="el-icon-back"
        @click="$router.push({path: `/app/tenants/${$route.params.id}/customers`})">
        {{ $t('settings.backToCustomers') }}
      </base-button>
      <base-button
        type="primary"
        icon="el-icon-tickets"
        @click="$router.push({path: `/app/tenants/${$route.params.id}/customers/${$route.params.customerId}/accounts`})">
        {{ $t('settings.showCustomerAccounts') }}
      </base-button>
      <base-button
        type="danger"
        icon="el-icon-delete"
        @click="deleteDialogVisible = true">
        {{ $t('settings.deleteCustomer') }}
      </base-button>
    </div>

    <h2>{{ $t('settings.editCustomer') }}</h2>

    <customer-form-component
      :customer="customer"
      mode="edit"
      @submit="onSubmit"
      :loading="loading"
      @add-ad-connection="onAddAdConnection"
      @update-ad-connection="onUpdateAdConnection"
      @delete-ad-connection="onDeleteAdConnection">
    </customer-form-component>

    <base-delete-dialog
      :visible.sync="deleteDialogVisible"
      :title="$t('settings.deleteCustomer')"
      :text="$t('settings.deleteCustomerInfoText')"
      :confirm-label="$t('settings.deleteCustomer')"
      :loading="deleting"
      @confirm="confirmDeleteCustomer">
    </base-delete-dialog>
  </div>
</template>

<script>
import {createNamespacedHelpers} from 'vuex';
import CustomerFormComponent from '@/components/settings/customer/CustomerForm';
import BaseButton from '@/components/common/BaseButton';
import BaseDeleteDialog from '@/components/common/BaseDeleteDialog';
import {customerService} from "@/services/CustomerService.class";

const tenantStateMappers = createNamespacedHelpers('tenant');

export default {
  name: "EditTenantCustomer",
  components: {CustomerFormComponent, BaseButton, BaseDeleteDialog},
  data() {
    return {
      customer: null,
      loading: false,
      deleteDialogVisible: false,
      deleting: false
    }
  },
  mounted() {
    this.getCustomer();
  },
  methods: {
    getCustomer() {
      this.loading = true;
      customerService.getById(this.$route.params.customerId)
          .then((response) => {
            this.customer = response.data;
          })
          .catch(() => {
            this.$message.error(this.$t('settings.genericHttpError'));
            this.$router.push({path: `/app/tenants/${this.$route.params.id}/customers`});
          })
          .finally(() => {
            this.loading = false;
          });
    },
    onSubmit(customer) {
      this.loading = true;
      customerService.patch(customer)
          .then(() => {
            this.$message.success(this.$t('settings.patchSuccess'));
            this.getCustomer(); // Refresh customer data
          })
          .catch(() => {
            this.$message.error(this.$t('settings.genericHttpError'));
          })
          .finally(() => {
            this.loading = false;
          });
    },
    confirmDeleteCustomer() {
      this.deleting = true;
      this.$store.dispatch('tenant/deleteCustomer', {
        tenantId: this.$route.params.id,
        customerId: this.$route.params.customerId
      }).then(() => {
        this.$message({
          type: 'success',
          message: this.$t('settings.customerDeleted')
        });
        this.deleteDialogVisible = false;
        this.$router.push({path: `/app/tenants/${this.$route.params.id}/customers`});
      }).catch(() => {
        this.$message.error(this.$t('settings.genericHttpError'));
      }).finally(() => {
        this.deleting = false;
      });
    },

    onAddAdConnection(adConnection) {
      customerService.addAdConnection(this.customer, adConnection).then(() => {
        this.$message({
          type: 'success',
          message: this.$t('settings.adConnectionAdded')
        });
        this.getCustomer();
      }).catch(() => {
        this.$message({
          type: 'error',
          message: this.$t('settings.genericHttpError')
        });
      });
    },

    onUpdateAdConnection(adConnection) {
      customerService.updateAdConnection(this.customer, adConnection).then(() => {
        this.$message({
          type: 'success',
          message: this.$t('settings.adConnectionUpdated')
        });
        this.getCustomer();
      }).catch(() => {
        this.$message({
          type: 'error',
          message: this.$t('settings.genericHttpError')
        });
      });
    },

    onDeleteAdConnection(adConnection) {
      customerService.deleteAdConnection(this.customer, adConnection).then(() => {
        this.$message({
          type: 'success',
          message: this.$t('settings.adConnectionDeleted')
        });
        this.getCustomer();
      }).catch(() => {
        this.$message({
          type: 'error',
          message: this.$t('settings.genericHttpError')
        });
      });
    },
  }
}
</script>

<style scoped lang="scss">
.page-actions {
  display: flex;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 1rem;
}
</style>
